/**
 * Keeps an open tab from running a CENTERLINE build that a deploy has since replaced.
 *
 * A single-page app only picks up a deploy when the page is reloaded, so a tab left open keeps
 * running whatever it first loaded -- including any bug the deploy fixed. This checks every few
 * minutes (and whenever the tab comes back into view) whether the index.html being served now
 * names a different main bundle than the one this tab is running.
 *
 * - A different bundle is an update: listeners (VersionUpdateNotice) offer a reload and take one
 *   at the next navigation, so no one loses work they are in the middle of.
 * - A different bundle whose index.html also carries a higher reload generation is the kill
 *   switch: the tab reloads immediately, hidden or not. Raise the generation in public/index.html
 *   only when open tabs must not keep running the old build (e.g. it floods the API); it can cost
 *   someone an unsaved form.
 *
 * index.html is what gets compared because it is the one file CloudFront never caches (see
 * cdk/lib/stacks/frontend-stack.ts). Builds are compared for difference, not order, so a
 * rollback to an older build is picked up as well.
 */

export const RELOAD_GENERATION_META_NAME = 'centerline-reload-generation';

/** Session storage key recording the bundle this tab last reloaded to get. */
export const RELOADED_FOR_BUNDLE_KEY = 'centerline-reloaded-for-bundle';

export const VERSION_CHECK_INTERVAL_MS = 5 * 60 * 1000;

const MAIN_BUNDLE_PATTERN = /\/static\/js\/(main\.[0-9a-f]+\.js)$/;

export type BuildInfo = {
  /** The hashed main bundle file name, e.g. main.7e25f96e.js; undefined on the dev server. */
  bundle?: string;
  reloadGeneration: number;
};

export type BuildComparison = 'current' | 'update-available' | 'reload-required';

export const readBuildInfo = (doc: Document): BuildInfo => {
  const bundle = Array.from(doc.querySelectorAll('script[src]'))
    .map((script) => script.getAttribute('src')?.match(MAIN_BUNDLE_PATTERN)?.[1])
    .find(Boolean);
  const generation = Number(
    doc.querySelector(`meta[name="${RELOAD_GENERATION_META_NAME}"]`)?.getAttribute('content'),
  );
  return { bundle, reloadGeneration: Number.isFinite(generation) ? generation : 0 };
};

export const parseBuildInfo = (html: string): BuildInfo =>
  readBuildInfo(new DOMParser().parseFromString(html, 'text/html'));

export const compareBuilds = (running: BuildInfo, latest: BuildInfo): BuildComparison => {
  // Without a hashed bundle on both sides there is nothing to compare (the dev server, or an
  // index.html that did not parse), and guessing would risk reloading tabs for nothing.
  if (!running.bundle || !latest.bundle || running.bundle === latest.bundle) return 'current';
  return latest.reloadGeneration > running.reloadGeneration
    ? 'reload-required'
    : 'update-available';
};

type Listener = (latest: BuildInfo) => void;

export type VersionCheckOptions = {
  runningDocument?: Document;
  fetchLatestIndexHtml?: () => Promise<string>;
  reload?: () => void;
  intervalMs?: number;
};

const fetchIndexHtml = async () => {
  const response = await fetch(`${process.env.PUBLIC_URL || ''}/index.html`, {
    cache: 'no-store',
  });
  if (!response.ok) throw new Error(`index.html returned ${response.status}`);
  return response.text();
};

let listeners = new Set<Listener>();
let pendingUpdate: BuildInfo | undefined;
let reloadPage: () => void = () => window.location.reload();

const readReloadedForBundle = () => {
  try {
    return sessionStorage.getItem(RELOADED_FOR_BUNDLE_KEY);
  } catch {
    return null;
  }
};

/**
 * Reloads to pick up `latest`, unless this tab already reloaded for that same bundle and is
 * still not running it. That happens when the reload is served a stale page (a cache between
 * here and S3), and reloading again would only repeat it -- a reload loop instead of a fix.
 * Returns whether it reloaded.
 */
export const reloadForBuild = (latest: BuildInfo): boolean => {
  if (!latest.bundle || readReloadedForBundle() === latest.bundle) return false;
  try {
    sessionStorage.setItem(RELOADED_FOR_BUNDLE_KEY, latest.bundle);
  } catch {
    // Without storage there is no loop guard, so only reload when someone asks for it.
    return false;
  }
  reloadPage();
  return true;
};

/** Reloads unconditionally, for a person who asked for it. */
export const reloadNow = () => reloadPage();

/** Calls `listener` once an update is found (straight away if one already has been). */
export const onUpdateAvailable = (listener: Listener) => {
  listeners.add(listener);
  if (pendingUpdate) listener(pendingUpdate);
  return () => {
    listeners.delete(listener);
  };
};

/** Starts checking for a newer build. Returns a function that stops it. */
export const startVersionCheck = ({
  runningDocument = document,
  fetchLatestIndexHtml = fetchIndexHtml,
  reload = () => window.location.reload(),
  intervalMs = VERSION_CHECK_INTERVAL_MS,
}: VersionCheckOptions = {}) => {
  reloadPage = reload;
  const running = readBuildInfo(runningDocument);
  let checking = false;

  const check = async () => {
    if (checking) return;
    checking = true;
    try {
      const latest = parseBuildInfo(await fetchLatestIndexHtml());
      const comparison = compareBuilds(running, latest);
      if (comparison === 'reload-required' && reloadForBuild(latest)) return;
      if (comparison !== 'current' && pendingUpdate?.bundle !== latest.bundle) {
        pendingUpdate = latest;
        listeners.forEach((listener) => listener(latest));
      }
    } catch {
      // Offline, mid-deploy, or a blip: try again next time. Deliberately not reported -- an
      // error report per failed check from every open tab is its own flood.
    } finally {
      checking = false;
    }
  };

  const onVisibilityChange = () => {
    if (document.visibilityState === 'visible') check();
  };

  // The dev server's bundle is not hashed, so there is never anything to compare against.
  if (!running.bundle) return () => undefined;

  const interval = setInterval(check, intervalMs);
  document.addEventListener('visibilitychange', onVisibilityChange);
  return () => {
    clearInterval(interval);
    document.removeEventListener('visibilitychange', onVisibilityChange);
    listeners = new Set();
    pendingUpdate = undefined;
    reloadPage = () => window.location.reload();
  };
};
